import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  IconButton,
  Badge,
  Popover,
  Typography,
  List,
  ListItem,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Business as BusinessIcon,
  Share as ShareIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

import {
  MY_NOTIFICATIONS_QUERY,
  UNREAD_NOTIFICATION_COUNT_QUERY,
  MARK_NOTIFICATION_READ_MUTATION,
  MARK_ALL_NOTIFICATIONS_READ_MUTATION,
  ACCEPT_WORKSPACE_INVITE_MUTATION,
  DECLINE_WORKSPACE_INVITE_MUTATION,
  ACCEPT_SHARE_REQUEST_MUTATION,
  DECLINE_SHARE_REQUEST_MUTATION,
} from '../services/graphql';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { AppNotification } from '../types';

export default function NotificationsButton() {
  const navigate = useNavigate();
  const { refetchWorkspaces } = useWorkspace();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const open = Boolean(anchorEl);

  // Fetch unread count
  const { data: countData, refetch: refetchCount } = useQuery(UNREAD_NOTIFICATION_COUNT_QUERY, {
    pollInterval: 30000, // Poll every 30 seconds
  });

  // Fetch notifications when popover opens
  const {
    data: notificationsData,
    loading,
    refetch: refetchNotifications,
  } = useQuery(MY_NOTIFICATIONS_QUERY, {
    skip: !open,
    fetchPolicy: 'network-only',
  });

  const [_markRead] = useMutation(MARK_NOTIFICATION_READ_MUTATION);
  const [markAllRead] = useMutation(MARK_ALL_NOTIFICATIONS_READ_MUTATION);
  const [acceptWorkspaceInvite] = useMutation(ACCEPT_WORKSPACE_INVITE_MUTATION);
  const [declineWorkspaceInvite] = useMutation(DECLINE_WORKSPACE_INVITE_MUTATION);
  const [acceptShareRequest] = useMutation(ACCEPT_SHARE_REQUEST_MUTATION);
  const [declineShareRequest] = useMutation(DECLINE_SHARE_REQUEST_MUTATION);

  const unreadCount = countData?.unreadNotificationCount || 0;
  const notifications: AppNotification[] = notificationsData?.myNotifications || [];

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAllRead = async () => {
    await markAllRead();
    refetchNotifications();
    refetchCount();
  };

  const handleAcceptInvite = async (notification: AppNotification) => {
    setActionLoading(notification.id);
    try {
      await acceptWorkspaceInvite({
        variables: { invitationId: notification.entityId },
      });
      await refetchWorkspaces();
      refetchNotifications();
      refetchCount();
    } catch (err) {
      console.error('Failed to accept invite:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeclineInvite = async (notification: AppNotification) => {
    setActionLoading(notification.id);
    try {
      await declineWorkspaceInvite({
        variables: { invitationId: notification.entityId },
      });
      refetchNotifications();
      refetchCount();
    } catch (err) {
      console.error('Failed to decline invite:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAcceptShare = async (notification: AppNotification) => {
    setActionLoading(notification.id);
    try {
      await acceptShareRequest({
        variables: { shareRequestId: notification.entityId },
      });
      refetchNotifications();
      refetchCount();
      // Navigate to vault to see the new item
      navigate('/vault');
      handleClose();
    } catch (err) {
      console.error('Failed to accept share:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeclineShare = async (notification: AppNotification) => {
    setActionLoading(notification.id);
    try {
      await declineShareRequest({
        variables: { shareRequestId: notification.entityId },
      });
      refetchNotifications();
      refetchCount();
    } catch (err) {
      console.error('Failed to decline share:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'WORKSPACE_INVITE':
        return <BusinessIcon color="primary" />;
      case 'SHARE_REQUEST':
        return <ShareIcon color="secondary" />;
      default:
        return <NotificationsIcon />;
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        sx={{ ml: 1 }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 480,
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Notifications</Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={handleMarkAllRead}>
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />

        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No notifications</Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, maxHeight: 360, overflow: 'auto' }}>
            {notifications.map((notification) => (
              <ListItem
                key={notification.id}
                sx={{
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  bgcolor: notification.status === 'unread' ? 'action.hover' : 'transparent',
                  borderBottom: 1,
                  borderColor: 'divider',
                  py: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 1.5 }}>
                  {getNotificationIcon(notification.type)}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {notification.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {notification.body}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatTime(notification.createdAt)}
                    </Typography>
                  </Box>
                </Box>

                {notification.status === 'unread' && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.5, ml: 4.5 }}>
                    {notification.type === 'WORKSPACE_INVITE' && (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          color="inherit"
                          startIcon={<CloseIcon fontSize="small" />}
                          onClick={() => handleDeclineInvite(notification)}
                          disabled={actionLoading === notification.id}
                        >
                          Decline
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CheckIcon fontSize="small" />}
                          onClick={() => handleAcceptInvite(notification)}
                          disabled={actionLoading === notification.id}
                        >
                          {actionLoading === notification.id ? 'Processing...' : 'Accept'}
                        </Button>
                      </>
                    )}
                    {notification.type === 'SHARE_REQUEST' && (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          color="inherit"
                          startIcon={<CloseIcon fontSize="small" />}
                          onClick={() => handleDeclineShare(notification)}
                          disabled={actionLoading === notification.id}
                        >
                          Decline
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CheckIcon fontSize="small" />}
                          onClick={() => handleAcceptShare(notification)}
                          disabled={actionLoading === notification.id}
                        >
                          {actionLoading === notification.id ? 'Processing...' : 'Accept'}
                        </Button>
                      </>
                    )}
                  </Box>
                )}
              </ListItem>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
}

